import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function EstudentFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const categories = [
    { id: "all", name: "Todos" },
    { id: "notes", name: "Anotações" },
    { id: "summaries", name: "Resumos" },
    { id: "exercises", name: "Exercícios" },
    { id: "exams", name: "Provas" },
    { id: "projects", name: "Projetos" },
    { id: "presentations", name: "Apresentações" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/estudent?page=${pageNum}&limit=12`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching estudent content:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  const getSubjectColor = (subject) => {
    const colors = {
      math: "#E91E63",
      physics: "#9C27B0",
      chemistry: "#673AB7",
      biology: "#4CAF50",
      history: "#FF9800",
      geography: "#00BCD4",
      literature: "#795548",
      english: "#2196F3",
      programming: "#607D8B",
    };
    return colors[subject] || "#3F51B5";
  };

  if (loading && posts.length === 0) {
    return <div className="estudent_feed_loading"><div className="estudent_loader"></div><span>Carregando materiais...</span></div>;
  }

  return (
    <div className="estudent_feed">
      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="estudent_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
          </svg>
          <h3>Nenhum material encontrado</h3>
          <p>Seja o primeiro a compartilhar!</p>
        </div>
      ) : (
        <div className="estudent_grid">
          {posts.map((post) => (
            <div key={post._id} className="estudent_card">
              <div className="estudent_card_header" style={{ borderLeftColor: getSubjectColor(post.estudentData?.subject) }}>
                <span className="estudent_subject">{post.estudentData?.subject || "Geral"}</span>
                <span className="estudent_type">{categories.find(c => c.id === post.estudentData?.type)?.name || "Material"}</span>
              </div>
              <h3>{post.estudentData?.title || post.text}</h3>
              <p className="estudent_description">{post.estudentData?.description}</p>
              {post.estudentData?.topics?.length > 0 && (
                <div className="estudent_topics">
                  {post.estudentData.topics.slice(0, 3).map((topic, i) => (
                    <span key={i}>{topic}</span>
                  ))}
                  {post.estudentData.topics.length > 3 && (
                    <span>+{post.estudentData.topics.length - 3}</span>
                  )}
                </div>
              )}
              <div className="estudent_meta">
                {post.estudentData?.pages && <span>{post.estudentData.pages} páginas</span>}
                {post.estudentData?.difficulty && (
                  <span className={`estudent_difficulty ${post.estudentData.difficulty}`}>
                    {post.estudentData.difficulty === "easy" ? "Fácil" : post.estudentData.difficulty === "medium" ? "Médio" : "Difícil"}
                  </span>
                )}
              </div>
              <div className="estudent_footer">
                <Link to={`/profile/${post.user.username}`} className="estudent_author">
                  <img src={post.user.picture} alt="" />
                  <div>
                    <span className="estudent_author_name">{post.user.first_name}</span>
                    <span className="estudent_date">
                      <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                    </span>
                  </div>
                </Link>
                <div className="estudent_stats">
                  <span>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                    {post.estudentData?.downloads || 0}
                  </span>
                  <span>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    {post.estudentData?.likes || 0}
                  </span>
                </div>
              </div>
              {post.estudentData?.fileUrl && (
                <a
                  href={post.estudentData.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="estudent_download_btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  Baixar Material
                </a>
              )}
              <div onClick={(e) => e.stopPropagation()}>
                <FeedPostMenu
                  post={post}
                  onEdit={handleEditPost}
                  onPostDeleted={handlePostDeleted}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="estudent_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais materiais"}
          </button>
        </div>
      )}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
